import * as React from 'react';

import { Button } from '@lightspeed/flame/Button';
import { Flex } from '@lightspeed/flame/Core';
import { Divider } from '@lightspeed/flame/Divider';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@lightspeed/flame/Modal';
import { Spinner } from '@lightspeed/flame/Spinner';
import { Text } from '@lightspeed/flame/Text';

import Decimal from 'app/src/data/base/Decimal';
import { Eventbus } from 'app/src/data/Eventbus';
import Format from 'app/src/data/Format';
import SaleManager from 'app/src/payments/SaleManager';
import Terminal from 'app/src/payments/terminals/Terminal';
import { translate } from 'pages/utils/translate';
import { socketClient } from 'pages/views/devices/socketClient';
import { CFD_MESSAGES } from 'pages/views/devices/socketClient/types';
import { transformPayload } from 'pages/views/devices/utils/sale-transformer';
import Monitoring from 'shared_modules/Monitoring';

import { TippingModalProps } from '.';

export const transformPayloadForCFD = ({
  saleManager,
  transactionAmount,
  tippableAmount,
}: {
  saleManager: SaleManager;
  transactionAmount: Decimal;
  tippableAmount: Decimal;
}) => {
  const saleJson = saleManager.sale.toJSON();

  const { tipOption1, tipOption2, tipOption3 } = saleJson.MetaData || {};
  const tipOptions = [
    { value: Format.parseInt(tipOption1, 0) },
    { value: Format.parseInt(tipOption2, 0) },
    { value: Format.parseInt(tipOption3, 0) },
    { value: 0 },
  ];
  const tip = {
    tipOptions,
    customTip: true, // Todo: remove custom tip from CFD
    paymentTotal: transactionAmount.toString(),
    tippableAmount: tippableAmount.toString(),
  };

  return { sale: transformPayload(saleJson), tip };
};

export const TippingModal: React.FC<TippingModalProps> = ({
  terminal,
  saleManager,
  transactionAmount,
  tippableAmount,
  onTipComplete,
  onCancel,
}) => {
  const [modalState, setModalState] = React.useState({ open: true });
  const terminalName = terminal ? terminal.model.get('name') : translate('Credit/Debit Card');
  const { sale, tip } = React.useMemo(
    () => transformPayloadForCFD({ saleManager, transactionAmount, tippableAmount }),
    [saleManager, transactionAmount, tippableAmount]
  );

  const handleTipSkipped = () => {
    Monitoring.addEvent('tip.skip', 'TippingModal', {
      description: 'Merchant skipped the tip flow.',
      transactionAmount: transactionAmount?.toNumber() || null,
    });

    socketClient.send(CFD_MESSAGES.UPDATE_SALE, {
      sale,
      tip: null,
      paymentState: Terminal.TerminalEvents.PAYMENT_STARTED,
    });
    setModalState({ open: false });
    onTipComplete({
      tipAmount: new Decimal(0),
      totalAmountWithTip: transactionAmount,
    });
  };

  React.useEffect(() => {
    const handleTipConfirmed = (param: { tipAmount: string; totalAmountWithTip: string }) => {
      const { tipAmount = 0, totalAmountWithTip = 0 } = param || {};
      onTipComplete({
        tipAmount: new Decimal(tipAmount),
        totalAmountWithTip: new Decimal(totalAmountWithTip),
      });
    };

    const sendInitialTipDataToCFD = () => {
      socketClient.send(CFD_MESSAGES.TIP_FLOW_START, { sale, tip });
    };

    Eventbus.listen({ eventName: CFD_MESSAGES.TIP_CONFIRMED, callbackFn: handleTipConfirmed });
    sendInitialTipDataToCFD();

    return () => {
      Eventbus.unSubscribe(CFD_MESSAGES.TIP_CONFIRMED, handleTipConfirmed);
    };
  }, [onTipComplete, sale, tip]);

  const handleCancelTip = () => {
    socketClient.send(CFD_MESSAGES.UPDATE_SALE, { sale, tip: null });

    setModalState({ open: false });
    onCancel();
  };

  return (
    <Modal isOpen={modalState.open} closeOnEsc={false}>
      <ModalHeader showCloseButton={false} aria-label={`${terminalName}-terminal`}>
        {terminalName}
      </ModalHeader>

      <ModalBody>
        <Flex minWidth="412px" flexDirection="column" mb={4} alignItems="center">
          <Spinner
            color="blue-300"
            baseColor2="gray-200"
            size="96px"
            aria-label="loading-spinner"
          />
          <Text size="large" mt="4" fontWeight="bold">
            {translate('Waiting for customer')}
          </Text>
          <Text mt={4} size="small">
            {translate('Customer needs to confirm order and add tip.')}
          </Text>
          <Divider my={4} />
          <Text size="xlarge" fontWeight="bold" aria-label="pending balance">
            {Format.formatMoney(transactionAmount, 0)}
          </Text>
        </Flex>
      </ModalBody>

      <ModalFooter>
        <Flex justifyContent="flex-end">
          <Button
            id="cancel_tipping_pos"
            mr={2}
            aria-label="cancel button"
            onClick={handleCancelTip}
          >
            {translate('Cancel')}
          </Button>
          <Button id="skip_tipping_pos" aria-label="skip button" onClick={handleTipSkipped}>
            {translate('Skip')}
          </Button>
        </Flex>
      </ModalFooter>
    </Modal>
  );
};
